"use client";

import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { formatDateLong, formatTimeRange } from "@/lib/format/date";
import {
  Package,
  Calendar,
  MapPin,
  Eye,
  ChevronRight,
  Shirt,
  Clock,
} from "lucide-react";

interface OrderCardProps {
  order: {
    id: string;
    publicId: string;
    orderType: string;
    status: string;
    createdAt: string;
    promisedDeliveryAt?: string | null;
    area: {
      name: string;
    };
    address: {
      line1: string;
      city: string;
    };
    items?: { garmentType: string; qty: number }[];
    pickupSlot?: {
      startAt: string;
      endAt: string;
    } | null;
  };
  onView?: () => void;
}

const statusConfig: Record<string, { color: string; label: string }> = {
  CREATED: { color: "bg-gray-100 text-gray-700", label: "Created" },
  PICKUP_SCHEDULED: { color: "bg-blue-100 text-blue-700", label: "Pickup Scheduled" },
  PICKUP_ASSIGNED: { color: "bg-cyan-100 text-cyan-700", label: "Pickup Assigned" },
  PICKUP_IN_PROGRESS: { color: "bg-orange-100 text-orange-700", label: "Pickup In Progress" },
  PICKED_UP: { color: "bg-teal-100 text-teal-700", label: "Picked Up" },
  RECEIVED_AT_WORKSHOP: { color: "bg-blue-100 text-blue-700", label: "At Workshop" },
  INSPECTION_IN_PROGRESS: { color: "bg-sky-100 text-sky-700", label: "Being Inspected" },
  QUOTE_SENT: { color: "bg-amber-100 text-amber-700", label: "Quote Sent" },
  AWAITING_PAYMENT: { color: "bg-yellow-100 text-yellow-700", label: "Awaiting Payment" },
  PAID: { color: "bg-green-100 text-green-700", label: "Paid" },
  IN_PRODUCTION: { color: "bg-blue-100 text-blue-700", label: "In Production" },
  QC: { color: "bg-sky-100 text-sky-700", label: "Quality Check" },
  READY_FOR_DELIVERY: { color: "bg-emerald-100 text-emerald-700", label: "Ready for Delivery" },
  DELIVERY_ASSIGNED: { color: "bg-cyan-100 text-cyan-700", label: "Delivery Assigned" },
  OUT_FOR_DELIVERY: { color: "bg-orange-100 text-orange-700", label: "Out for Delivery" },
  DELIVERED: { color: "bg-green-100 text-green-700", label: "Delivered" },
  DISPUTE_OPEN: { color: "bg-red-100 text-red-700", label: "Dispute Open" },
  CANCELLED: { color: "bg-red-100 text-red-700", label: "Cancelled" },
  RETURN_SCHEDULED: { color: "bg-orange-100 text-orange-700", label: "Return Scheduled" },
  RETURN_IN_PROGRESS: { color: "bg-orange-100 text-orange-700", label: "Return In Progress" },
  RETURNED: { color: "bg-slate-100 text-slate-600", label: "Returned" },
  CLOSED: { color: "bg-slate-100 text-slate-600", label: "Closed" },
};

export function OrderCard({ order, onView }: OrderCardProps) {
  const status = statusConfig[order.status] || statusConfig.CREATED;
  const totalItems = order.items?.reduce((sum, item) => sum + item.qty, 0) || 0;

  return (
    <Card className="hover:shadow-md transition-shadow">
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-4 flex-1 min-w-0">
            <div className="p-2.5 bg-primary/10 rounded-lg shrink-0">
              <Package className="h-5 w-5 text-primary" />
            </div>

            <div className="flex-1 min-w-0 space-y-3">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-semibold text-slate-900">
                  {order.publicId}
                </span>
                <Badge variant="outline" className="text-xs">
                  {order.orderType === "ALTERATION" ? "Alteration" : "Stitching"}
                </Badge>
                <Badge className={status.color}>{status.label}</Badge>
              </div>

              <div className="grid gap-2 text-sm sm:grid-cols-2 lg:grid-cols-3">
                {order.items && order.items.length > 0 && (
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Shirt className="h-4 w-4 shrink-0" />
                    <span className="truncate">
                      {totalItems} item{totalItems !== 1 ? "s" : ""} -{" "}
                      {order.items.map((i) => i.garmentType).join(", ")}
                    </span>
                  </div>
                )}

                {order.pickupSlot && (
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Calendar className="h-4 w-4 shrink-0" />
                    <span className="truncate">
                      Pickup: {formatDateLong(order.pickupSlot.startAt)}
                    </span>
                  </div>
                )}

                <div className="flex items-center gap-2 text-muted-foreground">
                  <MapPin className="h-4 w-4 shrink-0" />
                  <span className="truncate">
                    {order.address.line1}, {order.address.city}
                  </span>
                </div>

                {order.promisedDeliveryAt && (
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Clock className="h-4 w-4 shrink-0" />
                    <span className="truncate">
                      Est. Delivery: {formatDateLong(order.promisedDeliveryAt)}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {onView && (
            <Button variant="ghost" size="sm" onClick={onView} className="shrink-0">
              <Eye className="h-4 w-4 mr-1" />
              View
              <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
